import { computed, defineComponent, ref, watch } from "vue";
import type { IDataTableProps } from "./types";
import { DataHead } from "./components/data-head";
import { DataBody } from "./components/data-body";
import { DataFooter } from "./components/data-footer";
import { DataBanner } from "./components/data-banner";
import { tv } from "tailwind-variants";
import { uref } from "@/utils/reactive/uref";
import { Spinner } from "../spinner";
import { useTransformedRows } from "./hooks/useTransformedRows";
import { unwrapElementRef } from "@/utils/dom/unwrap";
import { useResponsive } from "~/composables/useResponsive";

const clazz = tv({
  slots: {
    base: "w-full h-full relative",
  },
  variants: {
    color: {
      gray: {
        base: "border border-gray-300",
      },
      white: {
        base: "bg-white",
      },
    },
    rounded: {
      true: {
        base: "rounded-md overflow-hidden",
      },
    },
    border: {
      true: {
        base: "border border-gray-300",
      },
    },
    shadow: {
      true: {
        base: "shadow-sm",
      },
    },
    hasBanner: {
      false: {
        base: 'border-t-[none]'
      }
    }
  },
});

export const DataTable = defineComponent<IDataTableProps>(
  (props) => {
    const responsive = useResponsive();
    const isLoading = computed(() => uref(props.loading) || false);
    const classes = clazz({
      rounded: !!props.rounded,
      border: !!props.border,
      shadow: !!props.shadow,
      color: props.variant as any,
      hasBanner: !!props.banner
    });

    const transformed = useTransformedRows({
      table: props.table,
      loading: isLoading,
    });

    const rows = computed(() => uref(transformed.rows));
    const headRows = computed(() => uref(transformed.headRows));

    const shouldShowBanner = computed(
      () => !!props.banner || !!props.table.options.search?.enabled,
    );
    const shouldShowFooter = computed(
      () => (!!props.table.paginator.value || props.footer) && !isLoading.value,
    );
    const shouldShowEmptyState = computed(
      () => rows.value.length <= 0 && !uref(props.loading),
    );

    const scrollElRef = ref();

    const resetScroll = () => {
      const el = unwrapElementRef(scrollElRef);
      if (!el) return;
      el.scrollTo({
        top: 0,
        behavior: "instant",
      });
    };

    const handlePageChange = () => {
      const paginator = uref(props.table.paginator);
      if (paginator && paginator.onPageChange) {
        paginator.onPageChange();
      }
      resetScroll();
    };

    watch(isLoading, (newValue, _oldValue) => {
      newValue ? resetScroll() : () => {};
    });

    const emptyStateContent = computed(() =>
      props.emptyState ? (
        <props.emptyState />
      ) : (
        <div class="flex items-center justify-center align-center">
          <span class="text-center space-y-1">
            <h1 class="text-gray-700 text-2xl font-medium">Empty</h1>
            <p class="text-gray-500">There is no data to show</p>
          </span>
        </div>
      ),
    );

    const loadingStateContent = computed(() => {
      const userState = unref(props.loadingState);
      if (userState) {
        if (typeof userState === "function") return <userState />;
        return userState;
      }
      return <Spinner />;
    });

    return () => {
      return (
        <div
          class={classes.base({})}
          style={{
            maxWidth: '100%',
            ...(responsive.isSmall.value ? {
              height: '100%',
              position: 'inherit',
              flexDirection: "column",
              display: "flex",
            flex: '0 1 auto',
            width: '100%',
            minWidth: 0
          } : {
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }),

          }}
        >
          {shouldShowBanner.value && (
            <DataBanner
              variant={props.banner?.variant || props.variant}
              banner={props.banner || {}}
              table={props.table}
              isLoading={isLoading}
            />
          )}
          <div
            class="w-full h-full pretty-scrollbar"
            ref={scrollElRef}
            style={{
              ...(responsive.isSmall.value ? {
                height: '100%',
                overflowX: 'auto',
                width: '100%',
                maxWidth: '100%'
              } : {
                overflow:
                  isLoading.value || shouldShowEmptyState.value
                    ? "hidden"
                    : "auto",
                position: "relative",
                flex: "1",
              }),
            }}
          >
            {!shouldShowEmptyState.value && (
              <table
                cellspacing="0"
                cellpadding="0"
                style={{
                  width: "100%",
                  textAlign: "left",
                  borderCollapse: "separate",
                  ...(props.layout
                    ? {
                        tableLayout: props.layout,
                      }
                    : {}),
                }}
              >
                {!isLoading.value && (
                  <DataHead
                    table={props.table}
                    borderless={props.borderlessHeader}
                    rows={headRows.value}
                    disableSorting={props.disableSorting}
                    borderlessTop={!props.banner}
                  />
                )}
                <DataBody
                  table={props.table}
                  variant={props.variant}
                  rows={rows}
                  blur={props.blur}
                />
              </table>
            )}
            {isLoading.value && (
              <div
                class="absolute left-0 top-0 w-full h-full flex justify-center items-center bg-white"
                style={{
                  ...(props.loaderOpacity
                    ? {
                        opacity: props.loaderOpacity,
                      }
                    : {}),
                }}
              >
                {loadingStateContent.value}
              </div>
            )}
            {shouldShowEmptyState.value && (
              <div
                class="w-full h-full flex justify-center items-center"
                style={{
                  // height: `calc(100% + ${HEAD_ROW_HEIGHT_PX * totalNumHeadRows.value}px)`,
                }}
              >
                {emptyStateContent.value}
              </div>
            )}
          </div>
          {shouldShowFooter.value &&
            (props.footer || (props.table.paginator &&
            props.table.paginator.value)) && (
              <DataFooter
                pagination={ (props.table.paginator &&
            props.table.paginator.value) ?  {
                  ...props.table.paginator.value,
                  onPageChange: handlePageChange,
                } : undefined}
                body={props.footer}
                variant={props.variant}
              />
            )}
        </div>
      );
    };
  },
  {
    props: [
      "variant",
      "banner",
      "table",
      "rounded",
      "border",
      "borderlessHeader",
      "loading",
      "emptyState",
      "shadow",
      "layout",
      "loaderOpacity",
      "disableSorting",
      "loadingState",
      "footer",
      "blur"
    ],
  },
);
